import { useState } from 'react';
import { Users, UsersRound, Building2 } from 'lucide-react';
import type { DojoExperienceLevel, DojoProfile } from '@/types/dojo';
import { cn } from '@/lib/utils';

/**
 * The picker gates the dojo only once the profile has loaded and says the level is
 * still unset — a missing profile means "not known yet", not "ask again".
 */
export function shouldPickLevel(profile: DojoProfile | null | undefined): boolean {
  return Boolean(profile) && profile?.experience_level == null;
}

interface LevelPickerProps {
  onSelect: (level: DojoExperienceLevel) => void;
  disabled?: boolean;
}

const LEVELS: Array<{
  level: DojoExperienceLevel;
  label: string;
  blurb: string;
  Icon: typeof Users;
}> = [
  {
    level: 'new_manager',
    label: 'New manager',
    blurb: 'Leading people is new — or about to be.',
    Icon: Users,
  },
  {
    level: 'mid_level',
    label: 'Mid-level leader',
    blurb: 'Teams, targets, and a boss of your own.',
    Icon: UsersRound,
  },
  {
    level: 'senior_executive',
    label: 'Senior / executive',
    blurb: 'Your decisions move the whole organization.',
    Icon: Building2,
  },
];

/**
 * First visit only, when `experience_level === null`. A full-pane step rather than
 * a modal: it is the first thing the dojo asks, not an interruption. One click
 * writes the level and the home screen takes over.
 */
export function LevelPicker({ onSelect, disabled = false }: LevelPickerProps) {
  const [chosen, setChosen] = useState<DojoExperienceLevel | null>(null);

  const handleSelect = (level: DojoExperienceLevel) => {
    if (disabled || chosen) return;
    setChosen(level);
    onSelect(level);
  };

  return (
    <div className="mx-auto w-full max-w-[720px] py-16 sm:py-[72px]">
      <h1 className="text-center font-body text-[28px] font-medium leading-[34px] tracking-[-0.015em] text-how-space-deep sm:text-[30px] sm:leading-9">
        Where are you in the arena?
      </h1>
      <p className="mx-auto mt-3 max-w-lg text-center font-body text-[14px] leading-[22px] text-how-text-secondary/85">
        The situations you practice get set at your altitude.
      </p>

      <div className="mt-8 grid gap-3 sm:grid-cols-3">
        {LEVELS.map(({ level, label, blurb, Icon }) => (
          <button
            key={level}
            type="button"
            onClick={() => handleSelect(level)}
            disabled={disabled || chosen !== null}
            className={cn(
              'group flex min-h-[168px] flex-col items-start rounded-2xl border border-how-space-deep/[0.06] bg-white/70 px-5 py-5 text-left shadow-surface transition-all',
              'hover:-translate-y-0.5 hover:border-how-accent-warm/25 hover:bg-white hover:shadow-lift',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-accent-warm/45 focus-visible:ring-offset-2 disabled:cursor-not-allowed',
              chosen === level && 'border-how-accent-warm/55 bg-white ring-2 ring-how-accent-warm/25',
              chosen !== null && chosen !== level && 'opacity-50'
            )}
          >
            <span className="text-how-accent-warm/75">
              <Icon size={17} strokeWidth={1.7} aria-hidden="true" />
            </span>
            <span className="mt-5 font-body text-[16px] font-medium leading-[22px] text-how-space-deep">
              {label}
            </span>
            <span className="mt-1.5 font-body text-[13px] leading-5 text-how-text-secondary/85">{blurb}</span>
          </button>
        ))}
      </div>

      <p className="mt-4 text-center font-body text-[12px] leading-5 text-how-text-secondary/85">
        You can change this later in Settings.
      </p>
    </div>
  );
}

export default LevelPicker;
